import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import * as fabric from 'fabric';
import { 
  AlignLeft, 
  AlignCenter, 
  AlignRight, 
  ArrowUp, 
  ArrowDown, 
  Magnet, 
  Maximize2,
  AlertCircle,
  X
} from 'lucide-react';
import { 
  A4_WIDTH, 
  A4_HEIGHT, 
  DUPLICATE_OFFSET, 
  PASTE_OFFSET, 
  EDITOR_STRINGS 
} from './constants';
import { useCanvasHistory } from './hooks/useCanvasHistory';
import { useSnapGuides } from './hooks/useSnapGuides';
import { useCanvasSelection } from './hooks/useCanvasSelection';
import { useCanvasAlignment, AlignmentType, DistributionType } from './hooks/useCanvasAlignment';

export interface EditorCanvasRef {
  addText: (text?: string, options?: Record<string, any>) => void;
  addRect: () => void;
  addCircle: () => void;
  addLine: () => void;
  addImage: (url: string) => void;
  deleteSelected: () => void;
  duplicateSelected: () => void;
  copySelected: () => void;
  pasteSelected: () => void;
  nudgeSelected: (dx: number, dy: number) => void;
  clearSelection: () => void;
  selectAll: () => void;
  bringToFront: () => void;
  sendToBack: () => void;
  bringForward: () => void;
  sendBackward: () => void;
  undo: () => void;
  redo: () => void;
  getCanvasJson: () => string;
  loadCanvasJson: (json: string) => Promise<void>;
  updateSelectedStyle: (styleProps: Record<string, any>) => void;
  getSelectedObjectProps: () => Record<string, any> | null;
  isEditingText: () => boolean;
  alignSelected: (alignment: AlignmentType) => void;
  distributeSelected: (direction: DistributionType) => void;
}

interface Props {
  initialJson?: string;
  onChange?: () => void;
  zoomLevel: number;
  /** Notifica a barra lateral sempre que a seleção muda (evita polling). */
  onSelectionChange?: (props: Record<string, any> | null) => void;
}

export const EditorCanvas = forwardRef<EditorCanvasRef, Props>(({ initialJson, onChange, zoomLevel, onSelectionChange }, ref) => {
  const canvasElRef = useRef<HTMLCanvasElement | null>(null);
  const fabricCanvasRef = useRef<fabric.Canvas | null>(null);
  const isUpdatingHistoryRef = useRef<boolean>(false);

  // Copy / Paste tracking
  const copiedObjectRef = useRef<fabric.FabricObject | null>(null);
  const pasteCountRef = useRef<number>(1);

  // Mantém sempre a versão mais recente do callback, já que o efeito de
  // inicialização do canvas roda só uma vez.
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;

  // User-facing toast notification error state
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 1. History Hook (Debounced & Max 50 states)
  const {
    saveHistoryImmediate,
    saveHistoryDebounced,
    undo,
    redo,
  } = useCanvasHistory(fabricCanvasRef, isUpdatingHistoryRef, onChange);

  // 2. Snap Guides Hook (Cached target calculations & zoom-adjusted threshold)
  const {
    snapEnabled,
    setSnapEnabled,
    handleObjectMoving,
    clearSnapTargets,
    renderGuideLines,
  } = useSnapGuides(fabricCanvasRef, zoomLevel);

  // 3. Selection Hook
  const {
    hasSelection,
    updateSelectionState,
    getFreshSelectedObjectProps,
    clearSelection,
    selectAll,
    bringToFront,
    sendToBack,
    bringForward,
    sendBackward,
  } = useCanvasSelection(fabricCanvasRef);

  // 4. Alignment Hook
  const { alignSelected, distributeSelected } = useCanvasAlignment(
    fabricCanvasRef,
    saveHistoryImmediate
  );

  // Native Fabric canvas initialization
  useEffect(() => {
    if (!canvasElRef.current) return;

    const canvas = new fabric.Canvas(canvasElRef.current, {
      width: A4_WIDTH * zoomLevel,
      height: A4_HEIGHT * zoomLevel,
      backgroundColor: '#ffffff',
      selection: true,
      preserveObjectStacking: true,
    });

    canvas.setZoom(zoomLevel);
    fabricCanvasRef.current = canvas;

    let isCancelled = false;

    // Load initial JSON payload safely
    const loadInitialState = async () => {
      if (!initialJson) {
        saveHistoryImmediate(canvas);
        return;
      }

      try {
        isUpdatingHistoryRef.current = true;
        const parsed = typeof initialJson === 'string' ? JSON.parse(initialJson) : initialJson;
        const res = canvas.loadFromJSON(parsed);
        if (res && typeof (res as any).then === 'function') {
          await res;
        }
        if (isCancelled) return;
        canvas.renderAll();
      } catch (e) {
        if (isCancelled) return;
        console.warn('Error parsing initial JSON payload:', e);
        setErrorMessage(EDITOR_STRINGS.JSON_LOAD_ERROR);
      } finally {
        if (!isCancelled) {
          isUpdatingHistoryRef.current = false;
          saveHistoryImmediate(canvas);
        }
      }
    };

    loadInitialState();

    // Event handlers
    const notificarSelecao = () => {
      onSelectionChangeRef.current?.(getFreshSelectedObjectProps());
    };

    const handleSelectionCreated = (e: { selected?: fabric.FabricObject[] }) => {
      updateSelectionState(e.selected?.[0]);
      notificarSelecao();
    };

    const handleSelectionUpdated = (e: { selected?: fabric.FabricObject[] }) => {
      updateSelectionState(e.selected?.[0]);
      notificarSelecao();
    };

    const handleSelectionCleared = () => {
      updateSelectionState(undefined);
      onSelectionChangeRef.current?.(null);
    };

    const handleObjectModified = () => {
      clearSnapTargets();
      notificarSelecao();
      if (!isUpdatingHistoryRef.current) {
        saveHistoryDebounced(canvas);
      }
    };

    canvas.on('selection:created', handleSelectionCreated);
    canvas.on('selection:updated', handleSelectionUpdated);
    canvas.on('selection:cleared', handleSelectionCleared);
    canvas.on('object:modified', handleObjectModified);
    canvas.on('object:moving', handleObjectMoving);
    canvas.on('after:render', renderGuideLines);
    canvas.on('mouse:up', clearSnapTargets);

    // EXPLICIT GUARD: Prevent double-recording history during initial load or programmatic loads
    canvas.on('object:added', () => {
      if (!isUpdatingHistoryRef.current) {
        saveHistoryDebounced(canvas);
      }
    });

    return () => {
      isCancelled = true;
      canvas.dispose();
      fabricCanvasRef.current = null;
    };
  }, []);

  // Sync zoom level natively via Fabric's setZoom & setDimensions
  useEffect(() => {
    const canvas = fabricCanvasRef.current;
    if (canvas) {
      canvas.setZoom(zoomLevel);
      canvas.setDimensions({
        width: A4_WIDTH * zoomLevel,
        height: A4_HEIGHT * zoomLevel,
      });
      canvas.renderAll();
    }
  }, [zoomLevel]);

  // Expose imperative methods to ref
  useImperativeHandle(ref, () => ({
    addText: (text = 'Novo Texto', options: Record<string, any> = {}) => {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;

      const iText = new fabric.IText(text, {
        left: 100,
        top: 100,
        fontSize: options.fontSize || 18,
        fontFamily: options.fontFamily || 'Arial',
        fill: options.fill || '#0f172a',
        fontWeight: options.fontWeight || 'normal',
        fontStyle: options.fontStyle || 'normal',
        ...options,
      });

      canvas.add(iText);
      canvas.setActiveObject(iText);
      canvas.renderAll();
    },

    addRect: () => {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;

      const rect = new fabric.Rect({
        left: 100,
        top: 100,
        width: 150,
        height: 100,
        fill: '#f1f5f9',
        stroke: '#0f172a',
        strokeWidth: 1,
        rx: 4,
        ry: 4,
      });

      canvas.add(rect);
      canvas.setActiveObject(rect);
      canvas.renderAll();
    },

    addCircle: () => {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;

      const circle = new fabric.Circle({
        left: 100,
        top: 100,
        radius: 50,
        fill: '#e2e8f0',
        stroke: '#0f172a',
        strokeWidth: 1,
      });

      canvas.add(circle);
      canvas.setActiveObject(circle);
      canvas.renderAll();
    },

    addLine: () => {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;

      const line = new fabric.Line([100, 100, 300, 100], {
        stroke: '#0f172a',
        strokeWidth: 2,
      });

      canvas.add(line);
      canvas.setActiveObject(line);
      canvas.renderAll();
    },

    addImage: (url: string) => {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;

      fabric.FabricImage.fromURL(url, { crossOrigin: 'anonymous' })
        .then((img) => {
          if (!img) return;
          if (img.width && img.width > 400) {
            img.scaleToWidth(300);
          }
          img.set({ left: 100, top: 100 });

          canvas.add(img);
          canvas.setActiveObject(img);
          canvas.renderAll();
        })
        .catch((err) => {
          console.error('Failed to load canvas image:', err);
          setErrorMessage(EDITOR_STRINGS.IMAGE_LOAD_ERROR);
        });
    },

    deleteSelected: () => {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;

      const activeObjects = canvas.getActiveObjects();
      if (activeObjects.length > 0) {
        activeObjects.forEach((obj) => canvas.remove(obj));
        canvas.discardActiveObject();
        canvas.renderAll();
        saveHistoryImmediate(canvas);
      }
    },

    duplicateSelected: () => {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;

      const activeObject = canvas.getActiveObject();
      if (!activeObject) return;

      activeObject.clone().then((cloned: fabric.FabricObject) => {
        canvas.discardActiveObject();
        cloned.set({
          left: (cloned.left || 0) + DUPLICATE_OFFSET,
          top: (cloned.top || 0) + DUPLICATE_OFFSET,
          evented: true,
        });

        if (cloned.type === 'activeSelection') {
          const activeSel = cloned as fabric.ActiveSelection;
          activeSel.canvas = canvas;
          activeSel.forEachObject((obj) => canvas.add(obj));
          if (activeSel.setCoords) activeSel.setCoords();
        } else {
          canvas.add(cloned);
        }

        canvas.setActiveObject(cloned);
        canvas.renderAll();
        saveHistoryImmediate(canvas);
      }).catch((err) => {
        console.error('Error duplicating object:', err);
      });
    },

    copySelected: () => {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;

      const activeObject = canvas.getActiveObject();
      if (!activeObject) return;

      activeObject.clone().then((cloned: fabric.FabricObject) => {
        copiedObjectRef.current = cloned;
        pasteCountRef.current = 1;
      }).catch((err) => {
        console.error('Error copying object:', err);
      });
    },

    pasteSelected: () => {
      const canvas = fabricCanvasRef.current;
      if (!canvas || !copiedObjectRef.current) return;

      copiedObjectRef.current.clone().then((cloned: fabric.FabricObject) => {
        canvas.discardActiveObject();
        const currentOffset = PASTE_OFFSET * pasteCountRef.current;
        pasteCountRef.current += 1;

        cloned.set({
          left: (copiedObjectRef.current?.left || 0) + currentOffset,
          top: (copiedObjectRef.current?.top || 0) + currentOffset,
          evented: true,
        });

        if (cloned.type === 'activeSelection') {
          const activeSel = cloned as fabric.ActiveSelection;
          activeSel.canvas = canvas;
          activeSel.forEachObject((obj) => canvas.add(obj));
          if (activeSel.setCoords) activeSel.setCoords();
        } else {
          canvas.add(cloned);
        }

        canvas.setActiveObject(cloned);
        canvas.renderAll();
        saveHistoryImmediate(canvas);
      }).catch((err) => {
        console.error('Error pasting object:', err);
      });
    },

    nudgeSelected: (dx: number, dy: number) => {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;

      const activeObject = canvas.getActiveObject();
      if (!activeObject) return;

      activeObject.set({
        left: (activeObject.left || 0) + dx,
        top: (activeObject.top || 0) + dy,
      });
      if (activeObject.setCoords) activeObject.setCoords();
      canvas.renderAll();
      saveHistoryDebounced(canvas);
    },

    clearSelection,
    selectAll,
    bringToFront,
    sendToBack,
    bringForward,
    sendBackward,
    undo,
    redo,

    getCanvasJson: () => {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return JSON.stringify({ version: '5.3.0', objects: [] });
      return JSON.stringify(canvas.toJSON());
    },

    loadCanvasJson: async (json: string) => {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;

      isUpdatingHistoryRef.current = true;
      try {
        const parsed = typeof json === 'string' ? JSON.parse(json) : json;
        const res = canvas.loadFromJSON(parsed);
        if (res && typeof (res as any).then === 'function') {
          await res;
        }
        canvas.renderAll();
        saveHistoryImmediate(canvas);
      } catch (e) {
        console.error('Error loading JSON onto canvas:', e);
        setErrorMessage(EDITOR_STRINGS.JSON_LOAD_ERROR);
      } finally {
        isUpdatingHistoryRef.current = false;
      }
    },

    updateSelectedStyle: (styleProps: Record<string, any>) => {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;

      const activeObject = canvas.getActiveObject();
      if (!activeObject) return;

      activeObject.set(styleProps);
      canvas.renderAll();
      updateSelectionState(activeObject);
      onSelectionChangeRef.current?.(getFreshSelectedObjectProps());
      saveHistoryDebounced(canvas);
    },

    getSelectedObjectProps: getFreshSelectedObjectProps,

    isEditingText: () => {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return false;
      const activeObj = canvas.getActiveObject() as any;
      return !!(activeObj && activeObj.isEditing);
    },

    alignSelected,
    distributeSelected,
  }));

  return (
    <div className="flex-1 overflow-auto bg-slate-900/60 p-6 md:p-12 relative flex flex-col items-center">
      {/* User-visible error notification banner */}
      {errorMessage && (
        <div className="sticky top-2 z-40 mb-4 bg-red-900/90 border border-red-500/50 text-red-100 px-4 py-2.5 rounded-xl shadow-lg flex items-center gap-3 max-w-md backdrop-blur-md transition-all">
          <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
          <span className="text-xs font-medium flex-1">{errorMessage}</span>
          <button
            onClick={() => setErrorMessage(null)}
            className="p-1 hover:bg-red-800/50 rounded-lg text-red-300 hover:text-white transition"
            aria-label="Fechar notificação de erro"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Floating Alignment Bar with Full Accessibility Labels */}
      <div className="sticky top-2 z-30 mb-4 bg-slate-900/90 backdrop-blur-md text-white border border-slate-700/80 rounded-2xl p-1.5 shadow-2xl flex items-center gap-1.5 transition-all">
        <button
          onClick={() => setSnapEnabled(!snapEnabled)}
          className={`px-3 py-1.5 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition ${
            snapEnabled
              ? 'bg-red-600 text-white shadow-md shadow-red-600/30'
              : 'bg-slate-800 text-slate-400 hover:text-white'
          }`}
          title={snapEnabled ? EDITOR_STRINGS.MAGNET_ACTIVE : EDITOR_STRINGS.MAGNET_OFF}
          aria-label={snapEnabled ? EDITOR_STRINGS.MAGNET_ACTIVE : EDITOR_STRINGS.MAGNET_OFF}
        >
          <Magnet className="w-3.5 h-3.5" />
          <span>{snapEnabled ? EDITOR_STRINGS.MAGNET_ACTIVE : EDITOR_STRINGS.MAGNET_OFF}</span>
        </button>

        <div className="h-5 w-px bg-slate-700 mx-1" />

        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 px-1 hidden sm:inline">
          {EDITOR_STRINGS.ALIGN_HEADER}
        </span>

        <button
          onClick={() => alignSelected('left')}
          disabled={!hasSelection}
          className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-200 hover:text-white disabled:opacity-40 disabled:hover:bg-transparent transition"
          title={EDITOR_STRINGS.ALIGN_LEFT}
          aria-label={EDITOR_STRINGS.ALIGN_LEFT}
        >
          <AlignLeft className="w-4 h-4" />
        </button>

        <button
          onClick={() => alignSelected('center-h')}
          disabled={!hasSelection}
          className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-200 hover:text-white disabled:opacity-40 disabled:hover:bg-transparent transition"
          title={EDITOR_STRINGS.ALIGN_CENTER_H}
          aria-label={EDITOR_STRINGS.ALIGN_CENTER_H}
        >
          <AlignCenter className="w-4 h-4" />
        </button>

        <button
          onClick={() => alignSelected('right')}
          disabled={!hasSelection}
          className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-200 hover:text-white disabled:opacity-40 disabled:hover:bg-transparent transition"
          title={EDITOR_STRINGS.ALIGN_RIGHT}
          aria-label={EDITOR_STRINGS.ALIGN_RIGHT}
        >
          <AlignRight className="w-4 h-4" />
        </button>

        <div className="h-4 w-px bg-slate-700 mx-0.5" />

        <button
          onClick={() => alignSelected('top')}
          disabled={!hasSelection}
          className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-200 hover:text-white disabled:opacity-40 disabled:hover:bg-transparent transition"
          title={EDITOR_STRINGS.ALIGN_TOP}
          aria-label={EDITOR_STRINGS.ALIGN_TOP}
        >
          <ArrowUp className="w-4 h-4" />
        </button>

        <button
          onClick={() => alignSelected('center-v')}
          disabled={!hasSelection}
          className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-200 hover:text-white disabled:opacity-40 disabled:hover:bg-transparent transition"
          title={EDITOR_STRINGS.ALIGN_CENTER_V}
          aria-label={EDITOR_STRINGS.ALIGN_CENTER_V}
        >
          <Maximize2 className="w-4 h-4" />
        </button>

        <button
          onClick={() => alignSelected('bottom')}
          disabled={!hasSelection}
          className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-200 hover:text-white disabled:opacity-40 disabled:hover:bg-transparent transition"
          title={EDITOR_STRINGS.ALIGN_BOTTOM}
          aria-label={EDITOR_STRINGS.ALIGN_BOTTOM}
        >
          <ArrowDown className="w-4 h-4" />
        </button>
      </div>

      <div className="min-h-full min-w-max flex items-start justify-center py-4 px-4">
        <div
          className="shadow-2xl bg-white rounded-sm border border-slate-300 shrink-0 my-auto overflow-hidden"
          style={{
            width: `${A4_WIDTH * zoomLevel}px`,
            height: `${A4_HEIGHT * zoomLevel}px`,
          }}
        >
          <canvas ref={canvasElRef} />
        </div>
      </div>
    </div>
  );
});
